import { formatCount, formatDecimal, tt } from '@agos/shared'

/**
 * `/vat-categories` — §9.5's DPH administration, which has no legacy counterpart.
 *
 * **FB-183. Two of the four VAT rates this franchise charges cannot be printed, and nothing says so until
 * a customer is standing at the till.**
 *
 * `FiskalReceiptService.slotFor` refuses outright:
 *
 * > Sadzba DPH „%s" (%s %%) nemá potvrdený index na pokladni. Zisti ho: `POST /api/v1/fiskal/raw` s
 * > `{"cmd":"FRINFO"}` vráti nastavenie DPH priamo zo zariadenia; číslo doplň cez
 * > `PATCH /api/v1/vat-categories/%d` s poľom `fiskalSlot`. **Bez neho by sa zaregistrovala nesprávna daň.**
 *
 * Measured: `A` (5 %) has slot 2, `B` (19 %) has slot 4, and **`C` (23 %) and `D` (0 %) have none**. An
 * order carrying either fails to fiscalise — not silently, but at the moment of sale.
 *
 * The register's slot numbers are not guessable: they are whatever *that device* was configured with, and
 * `FRINFO` is how you ask it. So the screen cannot fill them in; what it can do is say which categories are
 * unprintable, and give the operator the one command that answers.
 *
 * **And no category is the default.** `defaultCategory()` is
 * `SELECT … WHERE is_default AND is_active LIMIT 1`, and `is_default` is false on all four — so a product
 * with no category of its own falls through to `VatSlots.rateFor`, which reads the register's dine-in and
 * delivery rates and then the statutory 5/19 pair. That fallback is deliberate — *"a receipt has to print
 * with some rate, and printing nothing is not an option at a till"* — but it means the category table is
 * not what decides the rate today, and the screen says so rather than implying it is.
 */

export interface VatCategory {
  id: number
  name: string
  letter: string
  ratePercent: number
  fiskalSlot: number | null
  isDefault: boolean
  isActive: boolean
  sortOrder: number
}

function text(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null
}

function percent(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && value !== '') {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : 0
  }
  return 0
}

export function categoriesOf(payload: unknown): VatCategory[] {
  if (!Array.isArray(payload)) return []
  return payload
    .flatMap((entry) => {
      const row = entry as Record<string, unknown>
      if (typeof row.id !== 'number') return []
      return [
        {
          id: row.id,
          name: text(row.name) ?? '',
          letter: text(row.letter) ?? '',
          ratePercent: percent(row.rate),
          fiskalSlot: typeof row.fiskalSlot === 'number' ? row.fiskalSlot : null,
          isDefault: row.isDefault === true,
          isActive: row.isActive === true,
          sortOrder: typeof row.sortOrder === 'number' ? row.sortOrder : 0,
        },
      ]
    })
    .sort((left, right) => left.sortOrder - right.sortOrder || left.id - right.id)
}

/** FB-183: whether a receipt carrying this rate will actually print. */
export function isPrintable(category: VatCategory): boolean {
  return category.fiskalSlot !== null
}

export function unprintable(categories: readonly VatCategory[]): VatCategory[] {
  return categories.filter((category) => category.isActive && !isPrintable(category))
}

export function printabilityNote(category: VatCategory): string {
  if (isPrintable(category)) {
    return `${tt('Pokladňa túto sadzbu pozná ako index')} ${formatCount(category.fiskalSlot ?? 0)}.`
  }
  return tt(
    'Pokladňa pre túto sadzbu nemá potvrdený index, takže doklad s ňou sa nevytlačí - a zistí sa to až pri predaji.',
  )
}

/** The one command that answers it, from the backend's own error message. */
export const SLOT_DISCOVERY_COMMAND = 'FRINFO'

export function slotDiscoveryNote(): string {
  return `${tt('Index sa nedá uhádnuť - je to nastavenie konkrétnej pokladne. Zistíte ho príkazom')} ${SLOT_DISCOVERY_COMMAND} ${tt('(Pokladňa → Priamy príkaz), ktorý vráti nastavenie DPH priamo zo zariadenia.')}`
}

export function unprintableWarning(categories: readonly VatCategory[]): string {
  const broken = unprintable(categories)
  if (broken.length === 0) return ''
  const named = broken.map((category) => `${category.letter} (${formatDecimal(category.ratePercent, 2)} %)`)
  return `${formatCount(broken.length)} ${tt('z aktívnych sadzieb sa nedá vytlačiť')}: ${named.join(', ')}. ${tt('Objednávka s takou sadzbou zlyhá pri fiskalizácii, so zákazníkom pri pulte.')}`
}

// ─── The default, and what happens without one ───────────────────────────────

export function defaultCategory(categories: readonly VatCategory[]): VatCategory | null {
  return categories.find((category) => category.isDefault && category.isActive) ?? null
}

/**
 * What decides the rate when no category is marked default.
 *
 * `VatSlots.rateFor` reads the register's dine-in and delivery rates, then the register's own default, then
 * the statutory 5/19 pair — *"a receipt has to print with some rate, and printing nothing is not an option
 * at a till"*. Sound, and worth stating: with no default here, this table is not what decides the rate.
 */
export function defaultNote(categories: readonly VatCategory[]): string {
  const chosen = defaultCategory(categories)
  if (chosen !== null) {
    return `${tt('Základná sadzba je')} ${chosen.letter} - ${formatDecimal(chosen.ratePercent, 2)} %.`
  }
  return tt(
    'Žiadna sadzba nie je označená ako základná, takže o sadzbe rozhoduje nastavenie pokladne (jedlo na mieste / rozvoz) a až potom zákonná dvojica 5 / 19 %. Táto tabuľka o nej zatiaľ nerozhoduje.',
  )
}

// ─── The form ────────────────────────────────────────────────────────────────

export interface VatForm {
  name: string
  letter: string
  rate: string
  fiskalSlot: string
  isActive: boolean
  sortOrder: string
}

export function formOf(category: VatCategory): VatForm {
  return {
    name: category.name,
    letter: category.letter,
    rate: String(category.ratePercent),
    fiskalSlot: category.fiskalSlot === null ? '' : String(category.fiskalSlot),
    isActive: category.isActive,
    sortOrder: String(category.sortOrder),
  }
}

export function emptyForm(existing: readonly VatCategory[]): VatForm {
  const highest = existing.reduce((most, category) => Math.max(most, category.sortOrder), 0)
  return { name: '', letter: '', rate: '', fiskalSlot: '', isActive: true, sortOrder: String(highest + 10) }
}

export type VatProblem =
  | { kind: 'no-name' }
  | { kind: 'no-letter' }
  | { kind: 'bad-letter' }
  | { kind: 'duplicate-letter'; other: string }
  | { kind: 'bad-rate' }
  | { kind: 'rate-out-of-range' }
  | { kind: 'bad-slot' }
  | { kind: 'no-slot' }

/**
 * What the form refuses, and the one thing it only warns about.
 *
 * The **letter** is the shorthand the whole system carries: `VatSlots.letterFor` bands a rate into one,
 * orders snapshot it onto a line and invoicing recapitulates by it. Two categories sharing a letter would
 * make a recapitulation ambiguous, so a duplicate is refused.
 *
 * A **missing register slot** is only a warning: the category is legitimate and the server accepts it — it
 * just cannot be printed, which is what the whole screen is about. Refusing it would leave the operator
 * unable to record a rate they charge.
 */
export function problemsOf(form: VatForm, others: readonly VatCategory[]): VatProblem[] {
  const problems: VatProblem[] = []
  if (form.name.trim() === '') problems.push({ kind: 'no-name' })

  const letter = form.letter.trim().toUpperCase()
  if (letter === '') {
    problems.push({ kind: 'no-letter' })
  } else if (!/^[A-Z]$/.test(letter)) {
    problems.push({ kind: 'bad-letter' })
  } else {
    const clash = others.find((category) => category.letter.toUpperCase() === letter)
    if (clash !== undefined) problems.push({ kind: 'duplicate-letter', other: clash.name })
  }

  const rate = Number(form.rate.trim().replace(',', '.'))
  if (form.rate.trim() === '' || !Number.isFinite(rate)) {
    problems.push({ kind: 'bad-rate' })
  } else if (rate < 0 || rate > 100) {
    problems.push({ kind: 'rate-out-of-range' })
  }

  const slot = form.fiskalSlot.trim()
  if (slot === '') {
    problems.push({ kind: 'no-slot' })
  } else if (!/^\d+$/.test(slot)) {
    problems.push({ kind: 'bad-slot' })
  }
  return problems
}

/** The missing slot is the only one that does not stop the save. */
export function isBlocking(problem: VatProblem): boolean {
  return problem.kind !== 'no-slot'
}

export function canSave(form: VatForm, others: readonly VatCategory[]): boolean {
  return problemsOf(form, others).filter(isBlocking).length === 0
}

export function vatPayload(form: VatForm): Record<string, unknown> {
  const slot = form.fiskalSlot.trim()
  return {
    name: form.name.trim(),
    letter: form.letter.trim().toUpperCase(),
    rate: Number(form.rate.trim().replace(',', '.')),
    // Explicitly null when cleared, so the server unsets it rather than leaving the old index in place.
    fiskalSlot: slot === '' ? null : Number(slot),
    isActive: form.isActive,
    sortOrder: Number(form.sortOrder.trim()) || 0,
  }
}

export function problemMessage(problem: VatProblem): string {
  if (problem.kind === 'no-name') return tt('Názov je povinný.')
  if (problem.kind === 'no-letter') return tt('Písmeno je povinné.')
  if (problem.kind === 'bad-letter') return tt('Písmeno je jedno veľké písmeno A–Z.')
  if (problem.kind === 'duplicate-letter') {
    return `${tt('Toto písmeno už má')} ${problem.other} - ${tt('rekapitulácia DPH by ich nevedela rozlíšiť.')}`
  }
  if (problem.kind === 'bad-rate') return tt('Sadzba musí byť číslo.')
  if (problem.kind === 'rate-out-of-range') return tt('Sadzba je percento medzi 0 a 100.')
  if (problem.kind === 'bad-slot') return tt('Index pokladne je celé číslo.')
  return tt('Bez indexu pokladne sa doklad s touto sadzbou nevytlačí. Uložiť sa to dá, predať nie.')
}

/** `DELETE /vat-categories/{id}` is documented for a category *nothing is charged at*. */
export function deleteWarning(category: VatCategory): string {
  return `${tt('Server zmaže sadzbu len vtedy, keď na nej nič nie je účtované.')} ${tt('Ak sa na ňu niečo odkazuje, vypnite ju namiesto mazania - staré doklady si svoju sadzbu nesú so sebou.')} (${category.letter})`
}
